'use client'

import React from 'react'
import type { Answer } from '@/types/assessment'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

type Option = { value: number; label: string; color: string }
type Chip = { label: string; className: string; icon?: React.ReactNode }
type QuestionLike = {
  id: number
  text: string
  part?: 'A' | 'B'
  category?: string
  reverse?: boolean
}

interface Props {
  question: QuestionLike
  answer?: Answer
  onAnswerChange: (questionId: number, value: number) => void
  questionNumber: number
  options: Option[]
  chips?: Chip[]
  gridClass?: string
  badgeClass?: string
}

/**
 * Map Tailwind bg-* class names to hex values.
 * Dynamic Tailwind classes get purged by JIT - inline styles are the fix.
 */
const BG_COLOR_MAP: Record<string, string> = {
  // Gray
  'bg-gray-400': '#9ca3af',
  'bg-gray-500': '#6b7280',
  'bg-gray-600': '#4b5563',
  'bg-gray-700': '#374151',
  'bg-gray-800': '#1f2937',
  // Red
  'bg-red-400': '#f87171',
  'bg-red-500': '#ef4444',
  'bg-red-600': '#dc2626',
  'bg-red-700': '#b91c1c',
  // Orange
  'bg-orange-400': '#fb923c',
  'bg-orange-500': '#f97316',
  'bg-orange-600': '#ea580c',
  'bg-orange-700': '#c2410c',
  // Amber
  'bg-amber-400': '#fbbf24',
  'bg-amber-500': '#f59e0b',
  'bg-amber-600': '#d97706',
  'bg-amber-700': '#b45309',
  // Yellow
  'bg-yellow-400': '#facc15',
  'bg-yellow-500': '#eab308',
  'bg-yellow-600': '#ca8a04',
  'bg-yellow-700': '#a16207',
  // Lime
  'bg-lime-400': '#a3e635',
  'bg-lime-500': '#84cc16',
  'bg-lime-600': '#65a30d',
  // Green
  'bg-green-400': '#4ade80',
  'bg-green-500': '#22c55e',
  'bg-green-600': '#16a34a',
  'bg-green-700': '#15803d',
  // Emerald
  'bg-emerald-400': '#34d399',
  'bg-emerald-500': '#10b981',
  'bg-emerald-600': '#059669',
  // Teal
  'bg-teal-400': '#2dd4bf',
  'bg-teal-500': '#14b8a6',
  'bg-teal-600': '#0d9488',
  // Cyan
  'bg-cyan-400': '#22d3ee',
  'bg-cyan-500': '#06b6d4',
  'bg-cyan-600': '#0891b2',
  // Sky
  'bg-sky-400': '#38bdf8',
  'bg-sky-500': '#0ea5e9',
  'bg-sky-600': '#0284c7',
  // Blue
  'bg-blue-400': '#60a5fa',
  'bg-blue-500': '#3b82f6',
  'bg-blue-600': '#2563eb',
  'bg-blue-700': '#1d4ed8',
  // Indigo
  'bg-indigo-400': '#818cf8',
  'bg-indigo-500': '#6366f1',
  'bg-indigo-600': '#4f46e5',
  // Violet
  'bg-violet-400': '#a78bfa',
  'bg-violet-500': '#8b5cf6',
  'bg-violet-600': '#7c3aed',
  // Purple
  'bg-purple-400': '#c084fc',
  'bg-purple-500': '#a855f7',
  'bg-purple-600': '#9333ea',
  'bg-purple-700': '#7e22ce',
  // Fuchsia
  'bg-fuchsia-400': '#e879f9',
  'bg-fuchsia-500': '#d946ef',
  'bg-fuchsia-600': '#c026d3',
  // Pink
  'bg-pink-400': '#f472b6',
  'bg-pink-500': '#ec4899',
  'bg-pink-600': '#db2777',
  // Rose
  'bg-rose-400': '#fb7185',
  'bg-rose-500': '#f43f5e',
  'bg-rose-600': '#e11d48',
  // Slate
  'bg-slate-500': '#64748b',
  'bg-slate-600': '#475569',
  'bg-slate-700': '#334155',
}

/** Fallback: extract hex from a Tailwind bg class, or return a default */
const FALLBACK_COLOR = '#6366f1' // indigo-500

const QuestionCard: React.FC<Props> = ({
  question,
  answer,
  onAnswerChange,
  questionNumber,
  options,
  chips = [],
  gridClass = 'flex flex-col xs:flex-row xs:flex-wrap gap-2',
  badgeClass = 'w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-500 text-white text-xs sm:text-sm font-bold',
}) => {
  return (
    <Card className="mb-4 sm:mb-6 border border-gray-100 shadow-md ring-1 ring-border transition-transform will-change-transform hover:-translate-y-0.5">
      <CardContent className="p-4 sm:p-6">
        {/* Mobile: badge + chips inline on top; Desktop: badge on left side */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:gap-4">
          {/* Badge + chips row on mobile */}
          <div className="flex items-center gap-2 sm:gap-0 mb-2 sm:mb-0">
            <div className="flex-shrink-0">
              <div
                className={cn(badgeClass, 'flex items-center justify-center')}
              >
                {questionNumber}
              </div>
            </div>
            {/* Chips - inline with badge on mobile, below badge on desktop */}
            {chips.length > 0 && (
              <div className="flex sm:hidden flex-wrap items-center gap-1.5">
                {chips.map((chip, i) => (
                  <Badge
                    key={`${chip.label}-${i}`}
                    variant="secondary"
                    className={cn(
                      'px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap',
                      chip.className,
                    )}
                  >
                    {chip.icon ? (
                      <span className="mr-0.5" aria-hidden="true">
                        {chip.icon}
                      </span>
                    ) : null}
                    {chip.label}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Chips - desktop only (hidden on mobile since shown above) */}
            {chips.length > 0 && (
              <div className="hidden sm:flex flex-wrap items-center gap-2 mb-3">
                {chips.map((chip, i) => (
                  <Badge
                    key={`${chip.label}-${i}-desk`}
                    variant="secondary"
                    className={cn(
                      'px-2 py-1 text-xs font-medium whitespace-nowrap',
                      chip.className,
                    )}
                  >
                    {chip.icon ? (
                      <span className="mr-1" aria-hidden="true">
                        {chip.icon}
                      </span>
                    ) : null}
                    {chip.label}
                  </Badge>
                ))}
              </div>
            )}

            {/* Question text */}
            <h3 className="text-sm sm:text-base md:text-lg font-medium text-gray-900 mb-3 sm:mb-4 leading-relaxed">
              {question.text}
            </h3>

            {/* Options */}
            <div
              className={gridClass}
              role="group"
              aria-label={`Question ${questionNumber} options`}
            >
              {options.map((opt) => {
                const selected = answer?.value === opt.value
                const bgHex = BG_COLOR_MAP[opt.color] ?? FALLBACK_COLOR
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onAnswerChange(question.id, opt.value)}
                    aria-pressed={selected}
                    className={cn(
                      'justify-center px-2.5 py-2.5 sm:px-3 sm:py-3 text-xs sm:text-sm font-medium border-2 rounded-md',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-600',
                      'transition-all duration-200',
                      selected
                        ? 'text-white border-transparent shadow-md scale-[1.03] hover:opacity-95 active:opacity-95'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-100 active:bg-gray-200',
                    )}
                    style={selected ? { backgroundColor: bgHex } : undefined}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default QuestionCard
